/**
 * V2 source path: customer/midas/components/v2/MidasSidebarRailV2.tsx
 * V1 source: customer/midas/components/MidasSidebarHeader.tsx (collapsed 56px state, lines 84-101)
 *            + MidasFleetChips.tsx (collapsed icon column, lines 462-485)
 * Strategy: scratch-with-reused-handlers
 * Verified no-MUI: yes
 * Phase: 3
 *
 * Collapsed 56px rail variant. Used as the tablet default + as the desktop
 * collapsed state. Icon-only column with lucide icons + shadcn tooltips:
 *   - MIDAS hexagon mark (logo) at the top
 *   - Hexagon (Platform Mode) + active indicator dot
 *   - Grid (Fleet) — with terracotta dot if any machine has open SR
 *   - Clock (History)
 *   - Plus (new chat) — bottom
 *   - ChevronRight (expand) — bottom-most
 *
 * Each tile is a 44×44px hit area (TOUCH_UX guidelines). Tooltips use the
 * shadcn Tooltip primitive (already installed), wrapped in a TooltipProvider
 * by MidasPageV2.
 *
 * Logic copied from V1:
 *   - Platform Mode click: startNewChat + setActiveThreadId(null) + clear context.
 *   - Fleet badge: any machine with activeServiceRequestCount > 0 lights up the dot.
 */
import React, { useCallback, useMemo } from "react";
import { ChevronRight, Clock, Grid3X3, Hexagon, Plus } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  setMachineContext,
  startNewChat,
} from "../../../../shared/chatBot/chatBotSlice";
import { setActiveThreadId } from "../../midasPageSlice";
import { Machine } from "../../../machinery/models/models";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";

interface MidasSidebarRailV2Props {
  /** Called when the user taps the expand chevron — parent decides what "expand" means
   *  (desktop = swap to expanded sidebar; tablet = open overlay drawer). */
  onExpand: () => void;
  /** Called when the user taps the Fleet/History tiles — parent may scroll/select tab. */
  onSelectTab?: (tab: "fleet" | "history") => void;
}

const RailIconTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  badge?: boolean;
  onClick: () => void;
}> = ({ icon, label, active = false, badge = false, onClick }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={active}
        className={`relative flex h-11 w-11 items-center justify-center rounded-md outline-none transition-colors duration-150 motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-midas-terracotta active:scale-[0.95] motion-reduce:active:scale-100 ${
          active
            ? "bg-white text-midas-graphite shadow-[0_1px_2px_rgba(44,38,32,0.06)]"
            : "text-midas-warm-graphite hover:bg-[rgba(44,38,32,0.06)]"
        }`}
        style={{
          borderLeft: active ? "3px solid #D4572B" : "3px solid transparent",
        }}
      >
        {icon}
        {badge && (
          <span
            aria-hidden="true"
            className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full"
            style={{
              backgroundColor: "#D4572B",
              boxShadow: "0 0 4px rgba(212,87,43,0.6)",
            }}
          />
        )}
      </button>
    </TooltipTrigger>
    <TooltipContent
      side="right"
      sideOffset={8}
      className="bg-midas-graphite text-white"
    >
      {label}
    </TooltipContent>
  </Tooltip>
);

const MidasSidebarRailV2: React.FC<MidasSidebarRailV2Props> = ({
  onExpand,
  onSelectTab,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) => translate(k as any, { defaultValue: f }) as string;

  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const isPlatformActive = !machineContext;

  // Any machine with open SRs → light the Fleet badge
  const fleetHasOpenSr = useMemo(() => {
    if (!machinesMap || typeof machinesMap !== "object") return false;
    for (const group of Object.values(machinesMap)) {
      if (Array.isArray(group)) {
        for (const m of group as Machine[]) {
          if ((m.activeServiceRequestCount || 0) > 0) return true;
        }
      }
    }
    return false;
  }, [machinesMap]);

  const handlePlatformClick = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    if (!isPlatformActive) {
      dispatch(setMachineContext(null));
    }
  }, [dispatch, isPlatformActive]);

  const handleNewChatClick = useCallback(() => {
    dispatch(startNewChat());
    dispatch(setActiveThreadId(null));
    dispatch(setMachineContext(null));
  }, [dispatch]);

  return (
    <nav
      role="navigation"
      aria-label={t("midas.aria.sidebar", "MIDAS sidebar navigation")}
      className="flex h-full w-14 flex-col items-center border-r border-[rgba(44,38,32,0.08)] bg-midas-bone py-3"
    >
      {/* Logo / brand mark */}
      <button
        type="button"
        onClick={onExpand}
        aria-label={t("midas.aria.sidebarExpanded", "MIDAS — expand sidebar")}
        className="flex h-11 w-11 items-center justify-center rounded-md outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
      >
        <span
          aria-hidden="true"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-midas-graphite-deep"
        >
          <span
            className="block h-2 w-2 rounded-full"
            style={{
              backgroundColor: "#D4572B",
              boxShadow: "0 0 4px rgba(212,87,43,0.6)",
            }}
          />
        </span>
      </button>

      <div className="mt-3 flex flex-col gap-1.5">
        <RailIconTile
          icon={<Hexagon className="h-4 w-4" strokeWidth={1.5} />}
          label={t("midas.v2.platformMode", "Platform Mode")}
          active={isPlatformActive}
          onClick={handlePlatformClick}
        />
        <RailIconTile
          icon={<Grid3X3 className="h-4 w-4" strokeWidth={1.5} />}
          label={t("midas.v2.fleet", "Fleet")}
          badge={fleetHasOpenSr}
          onClick={() => {
            if (onSelectTab) onSelectTab("fleet");
            onExpand();
          }}
        />
        <RailIconTile
          icon={<Clock className="h-4 w-4" strokeWidth={1.5} />}
          label={t("midas.v2.history", "History")}
          onClick={() => {
            if (onSelectTab) onSelectTab("history");
            onExpand();
          }}
        />
      </div>

      <div className="mt-auto flex flex-col gap-1.5">
        <RailIconTile
          icon={<Plus className="h-4 w-4" strokeWidth={1.75} />}
          label={t("midas.v2.newChat", "New chat")}
          onClick={handleNewChatClick}
        />
        <RailIconTile
          icon={<ChevronRight className="h-4 w-4" strokeWidth={1.75} />}
          label={t("midas.aria.sidebarExpanded", "Expand sidebar")}
          onClick={onExpand}
        />
      </div>
    </nav>
  );
};

export default MidasSidebarRailV2;
